import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import StorageIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import { updatePipeline } from '../api/client';
import { getTouchstreamConfigs, getTouchstreamStreams, getTouchstreamStreamOptions } from '../api/touchstream';

function EditPipelineForm({ pipeline, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    type: 'aws',
    aws_role_arn: '',
    aws_region: 'us-east-1',
    creator_username: '',
    encoding: {
      video_bitrates: [
        { name: 'high', bitrate: 5000000, width: 1920, height: 1080 }
      ],
      audio_bitrates: [
        { name: 'main', bitrate: 128000, sample_rate: 48000 }
      ]
    },
    touchstream_config_id: '',
    touchstream_stream_keys: [],
    header_check_key: '',
    proxy_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [touchstreamConfigs, setTouchstreamConfigs] = useState([]);
  const [touchstreamStreams, setTouchstreamStreams] = useState([]);
  const [originStreams, setOriginStreams] = useState([]);
  const [cdnStreams, setCdnStreams] = useState([]);
  const [loadingStreams, setLoadingStreams] = useState(false);
  const [headerGroupOptions, setHeaderGroupOptions] = useState([]);

  // Load Touchstream configs on mount
  useEffect(() => {
    const loadConfigs = async () => {
      try {
        const configs = await getTouchstreamConfigs();
        setTouchstreamConfigs(configs);
      } catch (err) {
        console.error('Failed to load Touchstream configs:', err);
      }
    };
    loadConfigs();
  }, []);

  // Helper to load streams and options for a config
  const loadStreamsForConfig = async (configId) => {
    setLoadingStreams(true);
    try {
      // Load streams and options in parallel
      const [streamsData, optionsData] = await Promise.all([
        getTouchstreamStreams(configId),
        getTouchstreamStreamOptions(configId)
      ]);

      setTouchstreamStreams(streamsData.streams || []);
      setOriginStreams(streamsData.origin_streams || []);
      setCdnStreams(streamsData.cdn_streams || []);

      // Extract header_group choices from OPTIONS response
      const headerGroupChoices = optionsData?.options?.actions?.POST?.header_group?.choices || [];
      setHeaderGroupOptions(headerGroupChoices);
    } catch (err) {
      console.error('Failed to load streams:', err);
      setTouchstreamStreams([]);
      setOriginStreams([]);
      setCdnStreams([]);
      setHeaderGroupOptions([]);
    } finally {
      setLoadingStreams(false);
    }
  };

  // Populate form with existing pipeline data
  useEffect(() => {
    if (pipeline) {
      setFormData({
        name: pipeline.name || '',
        type: pipeline.type || 'aws',
        aws_role_arn: pipeline.aws_role_arn || '',
        aws_region: pipeline.aws_region || 'us-east-1',
        creator_username: pipeline.creator_username || '',
        encoding: pipeline.encoding || {
          video_bitrates: [
            { name: 'high', bitrate: 5000000, width: 1920, height: 1080 }
          ],
          audio_bitrates: [
            { name: 'main', bitrate: 128000, sample_rate: 48000 }
          ]
        },
        touchstream_config_id: pipeline.touchstream_config_id || '',
        touchstream_stream_keys: pipeline.touchstream_stream_keys || [],
        header_check_key: pipeline.header_check_key || '',
        proxy_url: pipeline.proxy_url || ''
      });

      // Load streams if config is already set
      if (pipeline.touchstream_config_id) {
        loadStreamsForConfig(pipeline.touchstream_config_id);
      }
    }
  }, [pipeline]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleVideoBitrateChange = (index, field, value) => {
    const newVideoBitrates = [...formData.encoding.video_bitrates];
    newVideoBitrates[index][field] = field === 'bitrate' || field === 'width' || field === 'height'
      ? parseInt(value) || 0
      : value;
    setFormData({
      ...formData,
      encoding: {
        ...formData.encoding,
        video_bitrates: newVideoBitrates
      }
    });
  };

  const handleAudioBitrateChange = (index, field, value) => {
    const newAudioBitrates = [...formData.encoding.audio_bitrates];
    newAudioBitrates[index][field] = field === 'bitrate' || field === 'sample_rate'
      ? parseInt(value) || 0
      : value;
    setFormData({
      ...formData,
      encoding: {
        ...formData.encoding,
        audio_bitrates: newAudioBitrates
      }
    });
  };

  const addVideoBitrate = () => {
    setFormData({
      ...formData,
      encoding: {
        ...formData.encoding,
        video_bitrates: [
          ...formData.encoding.video_bitrates,
          { name: `custom_${formData.encoding.video_bitrates.length + 1}`, bitrate: 1500000, width: 1280, height: 720 }
        ]
      }
    });
  };

  const removeVideoBitrate = (index) => {
    if (formData.encoding.video_bitrates.length > 1) {
      const newVideoBitrates = formData.encoding.video_bitrates.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        encoding: {
          ...formData.encoding,
          video_bitrates: newVideoBitrates
        }
      });
    }
  };

  const addAudioBitrate = () => {
    setFormData({
      ...formData,
      encoding: {
        ...formData.encoding,
        audio_bitrates: [
          ...formData.encoding.audio_bitrates,
          { name: `audio_${formData.encoding.audio_bitrates.length + 1}`, bitrate: 96000, sample_rate: 48000 }
        ]
      }
    });
  };

  const removeAudioBitrate = (index) => {
    if (formData.encoding.audio_bitrates.length > 1) {
      const newAudioBitrates = formData.encoding.audio_bitrates.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        encoding: {
          ...formData.encoding,
          audio_bitrates: newAudioBitrates
        }
      });
    }
  };

  const handleTouchstreamConfigChange = async (e) => {
    const configId = e.target.value;
    setFormData({
      ...formData,
      touchstream_config_id: configId || null,
      touchstream_stream_keys: [], // Reset stream keys when config changes
      header_check_key: '' // Reset header check key when config changes
    });

    if (configId) {
      await loadStreamsForConfig(configId);
    } else {
      setTouchstreamStreams([]);
      setOriginStreams([]);
      setCdnStreams([]);
      setHeaderGroupOptions([]);
    }
  };

  const handleStreamKeysChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      touchstream_stream_keys: typeof value === 'string' ? value.split(',') : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await updatePipeline(pipeline.id, formData);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update pipeline');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Edit Pipeline</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Pipeline Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              autoFocus
            />

            <TextField
              label="Your Username"
              name="creator_username"
              value={formData.creator_username}
              onChange={handleChange}
              required
              fullWidth
              helperText="This will be used to tag AWS resources for cost tracking"
              placeholder="john.doe"
            />

            <FormControl fullWidth>
              <InputLabel>Pipeline Type</InputLabel>
              <Select
                name="type"
                value={formData.type}
                label="Pipeline Type"
                disabled
              >
                <MenuItem value="aws">AWS Media Services</MenuItem>
                <MenuItem value="norsk">Norsk (Docker)</MenuItem>
              </Select>
            </FormControl>

            {formData.type === 'aws' && (
              <>
                <TextField
                  label="AWS Role ARN"
                  name="aws_role_arn"
                  value={formData.aws_role_arn}
                  onChange={handleChange}
                  placeholder="arn:aws:iam::123456789012:role/MediaServicesRole"
                  fullWidth
                  helperText="IAM role with permissions for MediaLive, MediaPackage, and CloudFront"
                />

                <FormControl fullWidth>
                  <InputLabel>AWS Region</InputLabel>
                  <Select
                    name="aws_region"
                    value={formData.aws_region}
                    onChange={handleChange}
                    label="AWS Region"
                  >
                    <MenuItem value="us-east-1">US East (N. Virginia)</MenuItem>
                    <MenuItem value="us-west-2">US West (Oregon)</MenuItem>
                    <MenuItem value="eu-west-1">EU (Ireland)</MenuItem>
                    <MenuItem value="eu-central-1">EU (Frankfurt)</MenuItem>
                    <MenuItem value="ap-southeast-1">Asia Pacific (Singapore)</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            {/* Encoding Configuration - Available for both AWS and Norsk */}
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Encoding Configuration</Typography>
                <Chip
                  label={`${formData.encoding.video_bitrates.length} video + ${formData.encoding.audio_bitrates.length} audio`}
                  size="small"
                  sx={{ ml: 2 }}
                />
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Video Bitrates */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1">Video Bitrates</Typography>
                      <IconButton onClick={addVideoBitrate} size="small" sx={{ ml: 1 }}>
                        <AddIcon />
                      </IconButton>
                    </Box>
                    {formData.encoding.video_bitrates.map((video, index) => (
                      <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={6} md={2}>
                            <TextField
                              label="Name"
                              value={video.name}
                              onChange={(e) => handleVideoBitrateChange(index, 'name', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              label="Bitrate (bps)"
                              type="number"
                              value={video.bitrate}
                              onChange={(e) => handleVideoBitrateChange(index, 'bitrate', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6} sm={4} md={2}>
                            <TextField
                              label="Width"
                              type="number"
                              value={video.width}
                              onChange={(e) => handleVideoBitrateChange(index, 'width', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6} sm={4} md={2}>
                            <TextField
                              label="Height"
                              type="number"
                              value={video.height}
                              onChange={(e) => handleVideoBitrateChange(index, 'height', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6} sm={3} md={2}>
                            <Chip
                              label={`${(video.bitrate / 1000000).toFixed(1)}M`}
                              size="small"
                              color="primary"
                            />
                          </Grid>
                          <Grid item xs={6} sm={1} md={1}>
                            {formData.encoding.video_bitrates.length > 1 && (
                              <IconButton
                                onClick={() => removeVideoBitrate(index)}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Box>

                  {/* Audio Bitrates */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1">Audio Bitrates</Typography>
                      <IconButton onClick={addAudioBitrate} size="small" sx={{ ml: 1 }}>
                        <AddIcon />
                      </IconButton>
                    </Box>
                    {formData.encoding.audio_bitrates.map((audio, index) => (
                      <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              label="Name"
                              value={audio.name}
                              onChange={(e) => handleAudioBitrateChange(index, 'name', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              label="Bitrate (bps)"
                              type="number"
                              value={audio.bitrate}
                              onChange={(e) => handleAudioBitrateChange(index, 'bitrate', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              label="Sample Rate (Hz)"
                              type="number"
                              value={audio.sample_rate}
                              onChange={(e) => handleAudioBitrateChange(index, 'sample_rate', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6} sm={3} md={2}>
                            <Chip
                              label={`${(audio.bitrate / 1000)}k`}
                              size="small"
                              color="secondary"
                            />
                          </Grid>
                          <Grid item xs={6} sm={3} md={1}>
                            {formData.encoding.audio_bitrates.length > 1 && (
                              <IconButton
                                onClick={() => removeAudioBitrate(index)}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* CMSD Proxy Configuration */}
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">CMSD Proxy Configuration</Typography>
                {formData.proxy_url && (
                  <Chip
                    label="Configured"
                    size="small"
                    color="info"
                    sx={{ ml: 2 }}
                  />
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Proxy URL"
                    name="proxy_url"
                    value={formData.proxy_url}
                    onChange={handleChange}
                    fullWidth
                    placeholder="http://localhost:8080"
                    helperText="URL of the CMSD proxy for header injection. When set, enables CMSD header injection controls."
                  />
                  <Alert severity="info" sx={{ mt: 1 }}>
                    The CMSD proxy injects Common Media Server Data headers (CMSD-Static, CMSD-Dynamic) into responses.
                    Use this for demo scenarios that show how CMSD headers indicate stream quality.
                  </Alert>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Touchstream Integration */}
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Touchstream Integration</Typography>
                {formData.touchstream_config_id && (
                  <Chip
                    label={`${formData.touchstream_stream_keys?.length || 0} streams`}
                    size="small"
                    sx={{ ml: 2 }}
                  />
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Touchstream Config</InputLabel>
                    <Select
                      value={formData.touchstream_config_id || ''}
                      onChange={handleTouchstreamConfigChange}
                      label="Touchstream Config"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {touchstreamConfigs.map((config) => (
                        <MenuItem key={config.id} value={config.id}>
                          {config.name} ({config.context_type})
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Select a Touchstream configuration for error simulation</FormHelperText>
                  </FormControl>

                  {formData.touchstream_config_id && (
                    <>
                    <FormControl fullWidth>
                      <InputLabel>Stream Keys</InputLabel>
                      <Select
                        multiple
                        value={formData.touchstream_stream_keys || []}
                        onChange={handleStreamKeysChange}
                        label="Stream Keys"
                        disabled={loadingStreams}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((key) => {
                              const stream = touchstreamStreams.find(s => s.key === key);
                              const isOrigin = stream?.category === 'origin';
                              return (
                                <Chip
                                  key={key}
                                  icon={isOrigin ? <StorageIcon fontSize="small" /> : <CloudIcon fontSize="small" />}
                                  label={stream?.name || key}
                                  size="small"
                                  sx={{
                                    bgcolor: isOrigin ? 'rgba(255, 152, 0, 0.1)' : 'rgba(33, 150, 243, 0.1)',
                                    color: isOrigin ? '#ff9800' : '#2196f3',
                                    '& .MuiChip-icon': { color: 'inherit' }
                                  }}
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {loadingStreams ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Loading streams...
                          </MenuItem>
                        ) : touchstreamStreams.length === 0 ? (
                          <MenuItem disabled>No streams available</MenuItem>
                        ) : (
                          touchstreamStreams.map((stream) => (
                            <MenuItem key={stream.key} value={stream.key}>
                              {stream.name || stream.key} ({stream.stream_format || 'HLS'} - {stream.stream_cdn || 'CDN'})
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      <FormHelperText>
                        Select streams for error simulation. Origin streams (orange) and CDN streams (blue) can be failed independently.
                      </FormHelperText>
                    </FormControl>

                    {/* Header Check Key Selection */}
                    {headerGroupOptions.length > 0 && (
                      <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Header Check Key</InputLabel>
                        <Select
                          value={formData.header_check_key || ''}
                          onChange={(e) => setFormData({ ...formData, header_check_key: e.target.value })}
                          label="Header Check Key"
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {headerGroupOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.display_name} ({option.value})
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          Select a header group key for Header Check error simulation
                        </FormHelperText>
                      </FormControl>
                    )}
                    </>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.name || !formData.creator_username}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default EditPipelineForm;
